/**
 * Google Static Maps URL builder for trip list thumbnails (mirrors iOS GoogleMapsService).
 *
 * Requires VITE_GOOGLE_MAPS_API_KEY (HTTP-referrer–restricted browser key).
 */

export interface TripMapMarker {
  latitude: number;
  longitude: number;
  /** A–Z label */
  label: string;
  /** Start/end (or embark/debark) use green */
  isEndpoint: boolean;
}

function mapsApiKey(): string {
  return String(import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '').trim();
}

export function isGoogleMapsConfigured(): boolean {
  return mapsApiKey().length > 0;
}

export function buildTripStaticMapUrl(
  markers: TripMapMarker[],
  options?: {
    width?: number;
    height?: number;
    /** Draw a path between markers (non-cruise trips). Default false to match cruise list thumbs. */
    drawPath?: boolean;
  }
): string | null {
  const API_KEY = mapsApiKey();
  if (!API_KEY || markers.length === 0) return null;
  const width = options?.width ?? 200;
  const height = options?.height ?? 200;
  const params = new URLSearchParams({
    key: API_KEY,
    size: `${width}x${height}`,
    maptype: 'roadmap',
    scale: '2',
  });
  for (const marker of markers) {
    const color = marker.isEndpoint ? 'green' : 'red';
    const size = marker.isEndpoint ? 'mid' : 'tiny';
    params.append(
      'markers',
      `size:${size}|color:${color}|label:${marker.label}|${marker.latitude},${marker.longitude}`
    );
  }
  if (options?.drawPath && markers.length > 1) {
    const pathCoords = markers.map((m) => `${m.latitude},${m.longitude}`).join('|');
    params.append('path', `color:0x0000ff80|weight:2|${pathCoords}`);
  }
  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
}

export function markersFromTripCoords(
  cityIds: string[],
  cityCoords: Record<string, { latitude: number; longitude: number }> | undefined
): TripMapMarker[] {
  if (!cityCoords) return [];
  const withCoords: Array<{ latitude: number; longitude: number }> = [];
  for (const id of cityIds) {
    const coord = cityCoords[id];
    if (!coord) continue;
    if (!Number.isFinite(coord.latitude) || !Number.isFinite(coord.longitude)) continue;
    if (coord.latitude === 0 && coord.longitude === 0) continue;
    withCoords.push(coord);
  }
  return withCoords.map((coord, index) => ({
    latitude: coord.latitude,
    longitude: coord.longitude,
    label: index < 26 ? String.fromCharCode(65 + index) : String(index + 1),
    isEndpoint: index === 0 || index === withCoords.length - 1,
  }));
}
